// Shared helpers for the workflow builder: converting between the API view/DTO
// shapes and the local editable model, plus client-side validation that mirrors
// the server rules described in the integration guide.

let localIdCounter = 1;
export const nextLocalId = () => `local-${localIdCounter++}`;

export const currencyFormat = (value) => {
    if (value === null || value === undefined || value === '') return '∞';
    return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Number(value));
};

// Build an empty starter definition (single unbounded tier with one empty step).
export const emptyTiers = () => ([
    {
        id: nextLocalId(),
        maxAmountExclusive: null,
        steps: [
            { id: nextLocalId(), requiredApprovals: 1, approvers: [{ id: nextLocalId(), approverKind: 'USER', userId: '', groupId: '', approvalType: 'REVIEWER' }] },
        ],
    },
]);

// Convert a WorkflowVersionView (read model) into the local editable tier model.
export const versionToLocalTiers = (version) => {
    if (!version || !version.tiers || version.tiers.length === 0) {
        return emptyTiers();
    }
    const sorted = [...version.tiers].sort((a, b) => a.tierOrder - b.tierOrder);
    return sorted.map((tier) => ({
        id: nextLocalId(),
        maxAmountExclusive: tier.maxAmountExclusive,
        steps: [...tier.steps]
            .sort((a, b) => a.stepOrder - b.stepOrder)
            .map((step) => ({
                id: nextLocalId(),
                requiredApprovals: step.requiredApprovals,
                approvers: step.approvers.map((ap) => ({
                    id: nextLocalId(),
                    approverKind: ap.approverKind,
                    userId: ap.userId ?? '',
                    groupId: ap.groupId ?? '',
                    approvalType: ap.approvalType || 'REVIEWER',
                })),
            })),
    }));
};

// Derive the inclusive lower bound of each tier from the previous tier's upper bound.
export const minForTier = (tiers, index) => (index === 0 ? 0 : Number(tiers[index - 1].maxAmountExclusive));

// Convert the local model into the WorkflowVersionDTO expected by the API.
export const localTiersToDto = (tiers) => ({
    tiers: tiers.map((tier, tierIndex) => ({
        tierOrder: tierIndex,
        minAmountInclusive: minForTier(tiers, tierIndex),
        maxAmountExclusive: tierIndex === tiers.length - 1 ? null : Number(tier.maxAmountExclusive),
        steps: tier.steps.map((step, stepIndex) => ({
            stepOrder: stepIndex,
            requiredApprovals: Number(step.requiredApprovals),
            approvers: step.approvers.map((ap) => (
                ap.approverKind === 'GROUP'
                    ? { approverKind: 'GROUP', groupId: Number(ap.groupId), approvalType: ap.approvalType || 'REVIEWER' }
                    : { approverKind: 'USER', userId: Number(ap.userId), approvalType: ap.approvalType || 'REVIEWER' }
            )),
        })),
    })),
});

// Stable serialisation for comparing the current editor state against the last saved draft.
export const serializeDefinition = (tiers) => JSON.stringify(localTiersToDto(tiers));

// Resolve the distinct AUTHORISER-eligibility of a step's approver pool, used for
// soft warnings (the server enforces this authoritatively at publish time).
const resolveStepUsers = (step, users, groups) => {
    const userIds = new Set();
    step.approvers.forEach((ap) => {
        if (ap.approverKind === 'USER' && ap.userId) {
            userIds.add(Number(ap.userId));
        } else if (ap.approverKind === 'GROUP' && ap.groupId) {
            const group = groups.find((g) => g.id === Number(ap.groupId));
            group?.members?.forEach((m) => userIds.add(m.userId));
        }
    });
    const resolved = [...userIds]
        .map((id) => users.find((u) => u.id === id))
        .filter(Boolean);
    return resolved;
};

// Returns { errors: string[], warnings: string[] }.
export const validateTiers = (tiers, users, groups) => {
    const errors = [];
    const warnings = [];

    if (!tiers || tiers.length === 0) {
        errors.push('At least one value tier is required.');
        return { errors, warnings };
    }

    tiers.forEach((tier, index) => {
        const isLast = index === tiers.length - 1;
        const min = minForTier(tiers, index);
        const label = `Tier ${index + 1}`;

        if (isLast) {
            if (tier.maxAmountExclusive !== null) {
                errors.push(`${label}: the final tier must be unbounded (no upper limit).`);
            }
        } else {
            const max = tier.maxAmountExclusive;
            if (max === null || max === '' || Number.isNaN(Number(max))) {
                errors.push(`${label}: an upper bound is required.`);
            } else if (Number(max) <= min) {
                errors.push(`${label}: the upper bound must be greater than ${currencyFormat(min)}.`);
            }
        }

        if (!tier.steps || tier.steps.length === 0) {
            errors.push(`${label}: at least one approval step is required.`);
        }

        tier.steps.forEach((step, stepIndex) => {
            const stepLabel = `${label}, Step ${stepIndex + 1}`;
            const required = Number(step.requiredApprovals);
            if (!required || required < 1) {
                errors.push(`${stepLabel}: required approvals must be at least 1.`);
            }
            if (!step.approvers || step.approvers.length === 0) {
                errors.push(`${stepLabel}: at least one approver is required.`);
            }
            step.approvers.forEach((ap) => {
                if (ap.approverKind === 'USER' && !ap.userId) {
                    errors.push(`${stepLabel}: select a user for each user approver.`);
                }
                if (ap.approverKind === 'GROUP' && !ap.groupId) {
                    errors.push(`${stepLabel}: select a group for each group approver.`);
                }
            });

            const userIdsInStep = step.approvers
                .filter((ap) => ap.approverKind === 'USER' && ap.userId)
                .map((ap) => Number(ap.userId));
            if (userIdsInStep.length !== new Set(userIdsInStep).size) {
                errors.push(`${stepLabel}: the same user cannot be added more than once to a step.`);
            }

            // The final step of every tier must contain AUTHORISER approvers only.
            const isFinalStep = stepIndex === tier.steps.length - 1;
            if (isFinalStep && step.approvers?.length > 0) {
                const nonAuthorisers = step.approvers.filter((ap) => ap.approvalType !== 'AUTHORISER');
                if (nonAuthorisers.length > 0) {
                    errors.push(`${stepLabel}: the final step of a tier must use Authoriser approvers only.`);
                }
            }

            // Soft, membership-dependent check (server is authoritative at publish).
            const resolved = resolveStepUsers(step, users, groups);
            if (required && resolved.length > 0 && resolved.length < required) {
                warnings.push(`${stepLabel}: only ${resolved.length} eligible approver(s) but ${required} required.`);
            }
        });
    });

    return { errors, warnings };
};
